import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// eslint-disable-next-line no-restricted-imports
import { Button as BlueprintButton, type ButtonProps } from '@buildoutinc/blueprint-react/ui/Button';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Props extends Omit<ButtonProps, 'nativeButton' | 'size'> {
  disabledTooltip?: React.ReactNode;
  href?: string | null;
  icon?: IconDefinition | null;
  iconEnd?: IconDefinition | null;
  label?: string | null;
  size?: ButtonProps['size'];
  target?: React.HTMLAttributeAnchorTarget;
  tooltip?: React.ReactNode;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip.Content>['side'];
}

export const Button = React.forwardRef<HTMLButtonElement, Props>(function Button({
  children,
  disabled,
  disabledTooltip,
  href,
  icon,
  iconEnd,
  label,
  render,
  size,
  target,
  tooltip,
  tooltipPlacement,
  ...props
}: Props, ref) {
  const content = children || label;

  let blueprintSize: ButtonProps['size'] = size;
  // You can explicitly override size to 'default' for icon only buttons if you must, but it should
  // be rare and only for edge cases
  if (icon && !content && (size == null || size === 'sm')) {
    blueprintSize = size === 'sm' ? 'icon-sm' : 'icon';
  }

  if (href) {
    render = render || <a href={href} target={target} />;
  }

  const button = (
    <BlueprintButton
      disabled={disabled}
      nativeButton={!render}
      ref={ref}
      render={render}
      size={blueprintSize}
      {...props}
    >
      {icon && <FontAwesomeIcon icon={icon} />}
      {content}
      {iconEnd && <FontAwesomeIcon icon={iconEnd} />}
    </BlueprintButton>
  );

  if (disabled) {
    if (disabledTooltip) {
      // The wrapping span is necessary for tooltips to work on disabled buttons
      return (
        <Tooltip>
          <Tooltip.Trigger closeOnClick={false} delay={0} render={<span>{button}</span>} />
          <Tooltip.Content side={tooltipPlacement}>{disabledTooltip}</Tooltip.Content>
        </Tooltip>
      );
    }
  } else if (tooltip) {
    return (
      <Tooltip>
        <Tooltip.Trigger delay={0} render={button} />
        <Tooltip.Content side={tooltipPlacement}>{tooltip}</Tooltip.Content>
      </Tooltip>
    );
  }

  return button;
});
